import {type MouseEvent as ReactMouseEvent} from 'react'
import {useTranslation} from 'react-i18next'
import type {ProjectListItem} from '../../../common/api/generated/model/projectListItem'
import SceneGrid from '../templates/SceneGrid'
import TagAutocomplete from '../sharedWidgets/TagAutocomplete'
import EditableField from './EditableField'
import Icon from './Icon'

interface MyProjectCardProps {
  project: ProjectListItem
  // Stable mount timestamp so "x days ago" stays pure across re-renders.
  nowMs: number
  allTags: string[]
  onOpen: (project: ProjectListItem) => void
  onRename: (project: ProjectListItem, name: string) => void
  onDescriptionChange: (project: ProjectListItem, description: string) => void
  onTagsChange: (project: ProjectListItem, tags: string[]) => void
  onShareClick: (project: ProjectListItem) => void
  onDeleteClick: (project: ProjectListItem) => void
}

const ONE_DAY_MS = 1000 * 60 * 60 * 24

const MyProjectCard = ({
  project,
  nowMs,
  allTags,
  onOpen,
  onRename,
  onDescriptionChange,
  onTagsChange,
  onShareClick,
  onDeleteClick,
}: MyProjectCardProps) => {
  const {t} = useTranslation()

  const days = Math.max(0, Math.floor((nowMs - new Date(project.updatedAt).getTime()) / ONE_DAY_MS))
  const ageLabel = days === 0 ? t('myProjects.card.today') : t('myProjects.card.daysAgo', {count: days})

  const handleOpen = (e: ReactMouseEvent) => {
    e.preventDefault()
    onOpen(project)
  }

  return (
    <div className="tpl-card mp-card">
      <div className="tpl-thumb-frame">
        <SceneGrid thumbnails={project.sceneThumbnails} alt={project.name} />
        <a href="#" className="mp-thumb-open" onClick={handleOpen} aria-label={t('myProjects.card.openProject')}>
          <span className="label">
            <Icon name="play" size={12} /> {t('myProjects.card.openProject')}
          </span>
        </a>
      </div>
      <div className="mp-body">
        <EditableField
          className="mp-name"
          value={project.name}
          placeholder={t('myProjects.card.namePlaceholder')}
          ariaLabel={t('myProjects.card.nameAria')}
          editLabel={t('myProjects.card.edit')}
          saveLabel={t('myProjects.card.save')}
          onSave={(next) => onRename(project, next || t('myProjects.card.namePlaceholder'))}
        />
        <EditableField
          className="mp-desc"
          value={project.description ?? ''}
          placeholder={t('myProjects.card.descPlaceholder')}
          ariaLabel={t('myProjects.card.descAria')}
          editLabel={t('myProjects.card.edit')}
          saveLabel={t('myProjects.card.save')}
          multiline
          onSave={(next) => onDescriptionChange(project, next)}
        />
        {project.isPublic && (
          <div className="mp-tags">
            <TagAutocomplete
              value={project.tags}
              onChange={(next) => onTagsChange(project, next)}
              suggestions={allTags}
              allowCreate
              placeholder={t('myProjects.card.tagsPlaceholder')}
              ariaLabel={t('myProjects.card.tagsAria', {name: project.name})}
            />
          </div>
        )}
        <div className="mp-footer">
          <div className="mp-meta">
            <span className="mp-meta-item">{ageLabel}</span>
            {project.isPublic && (
              <>
                <span className="meta-dot" />
                <span className="mp-meta-item" title={t('myProjects.card.forks', {count: project.usageCount})}>
                  <Icon name="fork" size={12} /> {project.usageCount.toLocaleString()}
                </span>
              </>
            )}
          </div>
          <div className="mp-actions">
            {project.isPublic ? (
              <button type="button" className="mp-action-btn unshare" onClick={() => onShareClick(project)}>
                <Icon name="globe" size={12} /> {t('myProjects.card.unshare')}
              </button>
            ) : (
              <button type="button" className="mp-action-btn share" onClick={() => onShareClick(project)}>
                <Icon name="share" size={12} /> {t('myProjects.card.makePublic')}
              </button>
            )}
            <button
              type="button"
              className="mp-action-btn danger"
              onClick={() => onDeleteClick(project)}
              aria-label={t('myProjects.delete.short')}
            >
              <Icon name="trash" size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyProjectCard
